/**
 * The lift curve and drag polar (AD-007c). Deliberately a *modernized
 * point-mass* aero model, not a 6DOF coefficient set: the source data carries
 * one lift coefficient class per aircraft and a power/drag ratio, so anything
 * finer would be fake precision.
 *
 * The parasite-drag term is not a guess — aircraft-spec.ts solves it per
 * aircraft so that sea-level level flight at full throttle settles on the
 * `Vmax = 83.827·(power/drag) − 3.45` speed that flight-tunables.md §1.1
 * verified against the manual.
 *
 * **Sideslip contributes drag here and nothing else (AD-007i).** There is
 * deliberately no span-axis sideforce in this module: a translational force
 * along the span is perpendicular to the flight path, so it bleeds no speed
 * and curves the path — which is the path-curving job AD-007h took away from
 * lift and gave to the produced turn rate. Slip's cost is
 * `slipDragPerRad2·β²` in `dragCoefficient` below (path-aligned, so it costs
 * energy and curves nothing); slip's *restoration* is kinematic and lives in
 * `controls.ts` (`slipRelaxationRates`), where AD-007j swings the flight path
 * onto the nose without any force appearing in this sum at all.
 */

import type { Vec3 } from "./math3d.ts";
import { GRAVITY_MPS2, clamp } from "./units.ts";

export interface AeroSpec {
  readonly wingAreaM2: number;
  readonly clAlphaPerRad: number;
  readonly clMax: number;
  /** Angle of attack where the lift curve breaks: `clMax / clAlphaPerRad`. */
  readonly alphaStallRad: number;
  readonly parasiteDrag: number;
  readonly inducedDragFactor: number;
  readonly postStallClFactor: number;
  readonly postStallSpreadRad: number;
  readonly stallDragPerRad: number;
  /**
   * Extra drag per radian *squared* of sideslip (AD-007i). Slip's energy cost,
   * and the only thing slip does to the force sum — the translational span-axis
   * sideforce this replaces was perpendicular to the flight path, so it bled no
   * speed and quietly curved the path instead. The kinematic half of slip lives
   * in `controls.ts`'s `slipRelaxationRates`. Fitted to experiment 1's measured
   * skid bleed in AD-007j; see `slipDragPerRad2` in the tunable register.
   */
  readonly slipDragPerRad2: number;
}

export interface AirData {
  readonly airspeedMps: number;
  /** Angle of attack, positive nose above the flight path. */
  readonly alphaRad: number;
  /** Sideslip, positive when the flight path runs to the right of the nose. */
  readonly betaRad: number;
  readonly dynamicPressurePa: number;
}

/**
 * Air data from the body-frame velocity (x right, y up, forward −z).
 *
 * `wingIncidenceRad` is the angle between the wing's zero-lift line and the
 * fuselage reference the game reports as pitch attitude, and it is *added* to
 * the geometric angle: everything downstream — lift, drag, the stall break, the
 * spin entry, AD-007n's α reference — is a function of the angle **the wing**
 * is at, and this is the one place that angle is computed. Adding it here
 * rather than inside `liftCoefficient` is what keeps the change surgical: no
 * aerodynamic relation moves, because none of them is written in terms of the
 * fuselage. What moves is the *attitude* at which a given aerodynamic state is
 * flown, by exactly the incidence — which is the whole point, since the
 * original flies level with its nose 1.2° below the horizon and this port used
 * to need 4.75° above it (see `wingIncidenceRad` in `aero-tunables.ts`).
 *
 * It defaults to zero so that callers reasoning about pure geometry — and the
 * unit tests that check the lift curve's own shape — keep the unshifted angle.
 */
export function airDataFrom(bodyVelocity: Vec3, densityKgM3: number, wingIncidenceRad = 0): AirData {
  const airspeedMps = Math.sqrt(
    bodyVelocity.x * bodyVelocity.x + bodyVelocity.y * bodyVelocity.y + bodyVelocity.z * bodyVelocity.z,
  );
  const dynamicPressurePa = 0.5 * densityKgM3 * airspeedMps * airspeedMps;
  if (airspeedMps < 1e-6) {
    return { airspeedMps, alphaRad: wingIncidenceRad, betaRad: 0, dynamicPressurePa };
  }
  return {
    airspeedMps,
    alphaRad: Math.atan2(-bodyVelocity.y, -bodyVelocity.z) + wingIncidenceRad,
    betaRad: Math.asin(clamp(bodyVelocity.x / airspeedMps, -1, 1)),
    dynamicPressurePa,
  };
}

/**
 * The **fuselage's** angle to the airflow, recovered from the wing's.
 *
 * `readout.alphaRad` is the angle the *wing* is at, because that is the angle
 * every aerodynamic relation is written in. Three things downstream are not
 * aerodynamics at all — where a gun barrel points, where a wheel sits, which
 * way a wreck's nose falls — and each of them is a statement about the
 * *airframe*, which the incidence has just moved out from under the wing by
 * `wingIncidenceRad`. Each one calls this.
 *
 * The distinction is invisible while the incidence is zero, which is exactly
 * why it needs a name: the three call sites read identically either way, and
 * only this function's presence records that the choice was made rather than
 * defaulted. `docs/engineering/flight-model-design.md` AD-007o §6 lists them.
 */
export function bodyAlphaRad(wingAlphaRad: number, wingIncidenceRad: number): number {
  return wingAlphaRad - wingIncidenceRad;
}

/**
 * 1.0 up to the stall break, decaying to `postStallClFactor` over
 * `postStallSpreadRad` past it. Shared by `liftCoefficient` below and by the
 * produced-turn-rate mechanism (AD-007h, controls.ts): a departed wing cannot
 * be producing a turn, so the same collapse shape that costs the wing its lift
 * past the break also costs the produced heading rate its authority.
 */
export function postStallFactor(aero: AeroSpec, alphaRad: number): number {
  const magnitude = Math.abs(alphaRad);
  if (magnitude <= aero.alphaStallRad) return 1;
  const excess = Math.min(1, (magnitude - aero.alphaStallRad) / aero.postStallSpreadRad);
  return 1 - (1 - aero.postStallClFactor) * excess;
}

/**
 * Linear to the break, then a decay to `postStallClFactor · clMax` over
 * `postStallSpreadRad`. Continuous at the break by construction (the decay
 * factor is exactly 1 there), which the unit tests check — a discontinuity
 * would put a step change into the acceleration and break determinism-adjacent
 * assumptions about smooth integration.
 */
export function liftCoefficient(aero: AeroSpec, alphaRad: number): number {
  const magnitude = Math.abs(alphaRad);
  const sign = alphaRad < 0 ? -1 : 1;
  if (magnitude <= aero.alphaStallRad) {
    return aero.clAlphaPerRad * alphaRad;
  }
  return sign * aero.clMax * postStallFactor(aero, alphaRad);
}

/**
 * `betaRad` is explicit rather than defaulted so every caller has to state
 * whether its case carries sideslip: the steady-state analysis in
 * `performance.ts` solves symmetric flight and passes zero, while the
 * integrator passes what the air data actually shows.
 */
export function dragCoefficient(
  aero: AeroSpec,
  liftCl: number,
  alphaRad: number,
  betaRad: number,
): number {
  const stallExcess = Math.max(0, Math.abs(alphaRad) - aero.alphaStallRad);
  return (
    aero.parasiteDrag +
    aero.inducedDragFactor * liftCl * liftCl +
    aero.stallDragPerRad * stallExcess +
    aero.slipDragPerRad2 * betaRad * betaRad
  );
}

export function isStalled(aero: AeroSpec, alphaRad: number): boolean {
  return Math.abs(alphaRad) > aero.alphaStallRad;
}

/**
 * The wing has stopped flying — **either** way of running out of `C_Lmax`.
 *
 * The angle-of-attack test above is the one the model has always had. AD-007n
 * adds the other one, and it is not a convenience: the elevator now bounds the α
 * it will ask the wing for (`elevator-path.ts`'s `alphaReferenceRad`), which is
 * exactly what takes the every-airframe-departs census to zero — and left alone
 * it would also mean **no aeroplane could ever stall**, taking §4.5's own
 * "asymmetric entry, rudder held at the stall" and the whole spin model out of
 * the game with it.
 *
 * Below the 1-g stall speed the wing cannot hold the aeroplane up at *any* angle
 * inside the break, which is the same statement as α past the break and is the
 * definition every airspeed indicator's red line is drawn from. The two together
 * are the honest test, and the boundary is a measured one on the airframe this
 * was calibrated against: the Camel's 1-g stall sits at 42.5 mph
 * (flight-tunables.md §8.1 finding 6), the speed the original was last seen in
 * controlled flight at.
 *
 * Airborne only — a parked aeroplane is slow, not stalled.
 */
export function wingStalled(
  aero: AeroSpec,
  alphaRad: number,
  airspeedMps: number,
  massKg: number,
  densityKgM3: number,
): boolean {
  return isStalled(aero, alphaRad) || airspeedMps < stallSpeedMps(aero, massKg, densityKgM3);
}

/** Drag force in newtons at a given air state, given the lift coefficient already computed. */
export function dragForceN(aero: AeroSpec, air: AirData, liftCl: number): number {
  return (
    air.dynamicPressurePa *
    aero.wingAreaM2 *
    dragCoefficient(aero, liftCl, air.alphaRad, air.betaRad)
  );
}

/** Stall speed in level flight at a given density, in m/s. */
export function stallSpeedMps(aero: AeroSpec, massKg: number, densityKgM3: number): number {
  return Math.sqrt((2 * massKg * GRAVITY_MPS2) / (densityKgM3 * aero.wingAreaM2 * aero.clMax));
}
